import { calculateArea } from '~/helpers/unitHelpers';
import type { CanvasEvent, HoverEvent } from '../CanvasEvent';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { LngLat, Point } from '../SurfaceBase';
import VertexObject from '../VertexObject';

export default class DrawPolygonObject extends CanvasObject {
  vertices: VertexObject[] = [];

  pointerVertex: VertexObject;
  onPolygon: (o: LngLat[]) => void;

  constructor(id: string, surface: SurfaceBase, onPolygon: (p: LngLat[]) => void) {
    super(id, surface);
    this.pointerVertex = new VertexObject('pointer', this.surface, {
      point: { x: NaN, y: NaN },
      lngLat: { lng: NaN, lat: NaN },
    });
    this.addChild(this.pointerVertex);
    this.onPolygon = onPolygon;
  }

  onSurfaceTap = (e: CanvasEvent) => {
    const v = new VertexObject(`v-${this.vertices.length}`, this.surface, {
      point: e.point,
      lngLat: e.lngLat,
    });

    // if the mouse event is a double tap and the
    if (e.type === 'dblTap' && this.vertices.length >= 3) {
      this.onPolygon([...this.vertices.map((v) => v.lngLat), this.vertices[0].lngLat]);
    }

    // only the first vertex should be active
    if (this.vertices.length === 0) {
      v.interactive = true;
      v.on('tap', (e: CanvasEvent) => {
        this.onPolygon([...this.vertices.map((v) => v.lngLat), this.vertices[0].lngLat]);
        e.stopBubble();
      });
      v.on('hoverStart', (e) => {
        // override coords so it exactly draws over first vertex
        e.lngLat = this.vertices[0].lngLat;
        e.point = this.vertices[0].point;
        this.onPointerMove(e);
      });
    }

    //v.setLngLat(e.lngLat);
    this.vertices.push(v);
    this.surface.addObject(v);
  };

  onPointerMove = (e: HoverEvent) => {
    this.pointerVertex.setCoords(e);
    this.surface.renderAll();
  };

  // inspired by https://github.com/HarryStevens/geometric/blob/master/src/polygons/polygonCentroid.js
  centroid = (): Point => {
    const vertices = [...this.vertices, this.pointerVertex];
    let a = 0,
      x = 0,
      y = 0,
      l = vertices.length;

    for (let i = 0; i < l; i++) {
      const s = i === l - 1 ? 0 : i + 1,
        v0 = vertices[i],
        v1 = vertices[s],
        f = v0.point.x * v1.point.y - v1.point.x * v0.point.y;

      a += f;
      x += (v0.point.x + v1.point.x) * f;
      y += (v0.point.y + v1.point.y) * f;
    }

    const d = a * 3;

    return { x: x / d, y: y / d };
  };

  render(ctx: CanvasRenderingContext2D) {
    // Note: vertices are rendered by the object
    // lines
    if (this.vertices.length > 0) {
      function drawLine(p1: VertexObject, p2: VertexObject, dashed: boolean = false) {
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'red';
        dashed && ctx.setLineDash([10, 4]);
        ctx.moveTo(p1.point.x, p1.point.y);
        ctx.lineTo(p2.point.x, p2.point.y);
        ctx.stroke();
        ctx.setLineDash([]);
      }
      for (let i = 0; i < this.vertices.length - 1; i++) {
        drawLine(this.vertices[i], this.vertices[i + 1]);
      }

      // draw last dashed line
      drawLine(this.vertices[this.vertices.length - 1], this.pointerVertex, true);

      // polygon
      if (this.vertices.length >= 2) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.25)';
        ctx.beginPath();
        const firstPoint = this.vertices[0];
        ctx.moveTo(firstPoint.point.x, firstPoint.point.y);

        for (let i = 1; i < this.vertices.length; i++) {
          const p = this.vertices[i];
          ctx.lineTo(p.point.x, p.point.y);
        }
        ctx.lineTo(this.pointerVertex.point.x, this.pointerVertex.point.y);
        ctx.closePath();
        ctx.fill();
      }

      if (this.vertices.length >= 2) {
        // display area
        const a = calculateArea({
          unitSystem: this.getStore().currentUnitSystem,
          geometry: {
            type: 'Polygon',
            coordinates: [
              [
                ...this.vertices.map((c) => [c.lngLat.lng, c.lngLat.lat]),
                [this.pointerVertex.lngLat.lng, this.pointerVertex.lngLat.lat],
                [this.vertices[0].lngLat.lng, this.vertices[0].lngLat.lat],
              ],
            ],
          },
        });
        const centroid = this.centroid();

        const padding = 10;
        const fontSize = 14;
        ctx.font = `bold ${fontSize}px arial`; // NOTE: font size must be set before measureText to avoid dynamic scaling when object visibility changes
        const areaText = `${a.value.toLocaleString()} ${a.unit}`;
        const areaTextWidth = ctx.measureText(areaText).width;
        ctx.fillStyle = 'red';
        ctx.beginPath();
        ctx.roundRect(centroid.x, centroid.y, areaTextWidth + padding, fontSize + padding, [5]);
        ctx.fill();
        ctx.textBaseline = 'top';
        ctx.fillStyle = 'white';
        ctx.fillText(`${a.value.toLocaleString()} ${a.unit}`, centroid.x + padding / 2, centroid.y + padding / 2);
      }
    }
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    // polygon
    if (this.vertices.length >= 2) {
      ctx.fillStyle = color;
      ctx.beginPath();
      const firstPoint = this.vertices[0];
      ctx.moveTo(firstPoint.point.x, firstPoint.point.y);

      for (let i = 1; i < this.vertices.length; i++) {
        const p = this.vertices[i];
        ctx.lineTo(p.point.x, p.point.y);
      }
      ctx.lineTo(this.pointerVertex.point.x, this.pointerVertex.point.y);
      ctx.closePath();
      ctx.fill();
    }
  }

  onKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && this.vertices.length >= 3) {
      return this.onPolygon([...this.vertices.map((v) => v.lngLat), this.vertices[0].lngLat]);
    }
  };
}
